import { FC, useState } from 'react';
import { css } from '@emotion/react';
import { BusinessKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import isEqual from 'lodash-es/isEqual';
import { MultiLocationAccordionControl } from '@frontend/multi-location-accordion-control';
import { useAppScopeStore } from '@frontend/scope';
import { Accordion, ContentLoader, useModalControl } from '@frontend/design-system';
import { AiReceptionistTrackingIds } from '../../../../tracking-ids';
import { ErrorIndicator } from '../../common/error-indicator';
import { UnsavedWarningIndicator } from '../../common/unsaved-warning-indicator';
import { BusinessKnowledgeBulkEditModal } from './business-knowledge-bulk-edit-modal';
import { BusinessKnowledgeForm } from './business-knowledge-form';

type Props = {
  locationIds: string[];
  settings: Record<string, BusinessKnowledge>;
  initialSettings: Record<string, BusinessKnowledge>;
  locationErrors: Record<string, boolean>;
  isLoading?: boolean;
};

export const MultiLocationBusinessKnowledge: FC<Props> = ({
  locationIds,
  settings,
  initialSettings,
  locationErrors,
  isLoading,
}) => {
  const [filteredLocationIds, setFilteredLocationIds] = useState<string[]>(locationIds);
  const manageAllModalProps = useModalControl();
  const { getLocationName } = useAppScopeStore();

  return (
    <MultiLocationAccordionControl>
      <MultiLocationAccordionControl.Header
        filteredLocationIds={filteredLocationIds}
        openModal={manageAllModalProps.triggerProps.onClick}
        setFilteredLocationIds={setFilteredLocationIds}
        manageAllButtonProps={{
          disabled: isLoading,
          trackingId: AiReceptionistTrackingIds.businessKnowledgeManageAllButton,
        }}
      />

      <MultiLocationAccordionControl.Accordion
        startingValue={filteredLocationIds[0]}
        createAccordionBody={(locationId: string) => {
          const isDirty = !isEqual(settings[locationId], initialSettings[locationId]);
          const hasError = !!locationErrors[locationId];

          return (
            <Accordion.Item value={locationId}>
              <Accordion.Header css={styles.accordionHeader}>
                <Accordion.Header.Location title={getLocationName(locationId)} className='location-header' />
                {hasError && <ErrorIndicator mode='icon' />}
                {!hasError && isDirty && <UnsavedWarningIndicator mode='icon' />}
              </Accordion.Header>
              <Accordion.Body
                css={css`
                  padding: var(--fabric-space-padding-lg);
                  position: relative;
                `}
              >
                <ContentLoader show={isLoading} />
                <BusinessKnowledgeForm locationId={locationId} />
              </Accordion.Body>
            </Accordion.Item>
          );
        }}
        filteredLocationIds={filteredLocationIds}
      />

      {manageAllModalProps.modalProps.show && (
        <BusinessKnowledgeBulkEditModal modalControls={manageAllModalProps} filteredLocationIds={filteredLocationIds} />
      )}
    </MultiLocationAccordionControl>
  );
};

const styles = {
  accordionHeader: css`
    .header-container {
      padding: var(--fabric-space-gap-md) 0px var(--fabric-space-gap-md) var(--fabric-space-gap-md);
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      gap: var(--fabric-space-gap-sm);

      .location-header {
        justify-self: start;
      }
    }
  `,
};
